import React, { createContext, useContext, useState, useEffect } from 'react';
import { defaultServices, defaultPortfolio, defaultTestimonials, ServiceItem, PortfolioItem, TestimonialItem } from './data';
import { adminHeaders } from './api';

export interface FooterSection {
  title: string;
  links: Array<{ label: string; href: string }>;
}

export interface CustomPageItem {
  id: string;
  slug: string;
  title: string;
  content: string;
  isPublished: boolean;
}

export interface SiteConfig {
  theme: {
    primaryColor: string;
    accentColor: string;
    highlightColor: string;
    fontFamily: string;
  };
  contact: {
    phone: string;
    email: string;
    address: string;
    backgroundImage?: string;
    socials: Array<{ platform: string; url: string }>;
  };
  footer: {
    tagline: string;
    copyrightText: string;
    sections: FooterSection[];
  };
  home: {
    hero: {
      trustBadgeText: string;
      headlineLine1: string;
      headlineLine2: string;
      subtitle: string;
      primaryBtnText: string;
      secondaryBtnText: string;
      videoUrl?: string;
      clientLogos: Array<{ name: string; image: string }>;
    };
    stats: Array<{ number: string; label: string }>;
  };
  about: {
    whoWeAre: { text: string; image1: string; image2: string };
    expertise: { text: string; image1: string; image2: string };
    partners: { text: string; image1: string; image2: string };
    approach: { text: string; image1: string; image2: string };
    mission: { text: string; image1: string; image2: string };
    vision: { text: string; image1: string; image2: string };
    coreValues: Array<{
      title: string;
      description: string;
      icon: string;
      color: string;
    }>;
    promise: {
      tagline: string;
      text: string;
      quote: string;
    };
  };
  services: ServiceItem[];
  additionalServices: ServiceItem[];
  portfolio: PortfolioItem[];
  testimonials: TestimonialItem[];
  customPages: CustomPageItem[];
  smtp: {
    host: string;
    port: number;
    username: string;
    password: string;
    fromEmail: string;
    fromName: string;
    toEmail: string;
    encryption: 'tls' | 'ssl' | 'none';
  };
}

export const defaultSiteConfig: SiteConfig = {
  theme: {
    primaryColor: '#2575fc',
    accentColor: '#00a8ff',
    highlightColor: '#7cd024',
    fontFamily: 'Outfit',
  },
  contact: {
    phone: '+91 81559 74299',
    email: 'info@biggroww.com',
    address: 'BigGroww Agency, Anand, Gujarat, India. 388 001',
    backgroundImage: '',
    socials: [
      { platform: 'LinkedIn', url: 'https://linkedin.com' },
      { platform: 'Instagram', url: 'https://instagram.com' },
      { platform: 'Facebook', url: 'https://facebook.com' },
      { platform: 'GitHub', url: 'https://github.com' }
    ]
  },
  smtp: {
    host: '',
    port: 587,
    username: '',
    password: '',
    fromEmail: '',
    fromName: 'BigGroww Website',
    toEmail: '',
    encryption: 'tls' as const
  },
  footer: {
    tagline: 'Building digital excellence since 2020',
    copyrightText: '© 2026 BigGroww. All rights reserved.',
    sections: [
      {
        title: 'Services',
        links: [
          { label: 'Creative Development', href: '/services' },
          { label: 'App Development', href: '/services' },
          { label: 'Web Development', href: '/services' }
        ]
      },
      {
        title: 'Company',
        links: [
          { label: 'About Us', href: '/about' },
          { label: 'Careers', href: '/' },
          { label: 'Contact', href: '/contact' }
        ]
      },
      {
        title: 'Legal',
        links: [
          { label: 'Privacy Policy', href: '#/' },
          { label: 'Terms of Service', href: '#/' },
          { label: 'Cookie Policy', href: '#/' }
        ]
      }
    ]
  },
  home: {
    hero: {
      trustBadgeText: 'Trusted by forward-thinking teams.',
      headlineLine1: 'Create. Grow.',
      headlineLine2: 'Succeed.',
      subtitle: 'Your Vision, Our Strategy, Real Results. BigGroww is a full-service marketing agency creating innovative campaigns, designs, and digital solutions that boost brand growth and engagement.',
      primaryBtnText: 'Get Started',
      secondaryBtnText: 'View Our Work',
      videoUrl: '',
      clientLogos: [
        { name: 'Company 1', image: '' },
        { name: 'Company 2', image: '' },
        { name: 'Company 3', image: '' },
        { name: 'Company 4', image: '' },
        { name: 'Company 5', image: '' },
        { name: 'Company 6', image: '' }
      ]
    },
    stats: [
      { number: '600+', label: 'Projects Delivered' },
      { number: '50+', label: 'Team Members' },
      { number: '98%', label: 'Client Satisfaction' },
      { number: '10+', label: 'Years Experience' }
    ]
  },
  about: {
    whoWeAre: {
      text: 'At BigGroww, we believe every brand has the potential to become an industry leader with the right strategy, creativity, and technology. We are a full-service creative, digital marketing, and technology agency dedicated to helping businesses build powerful brands, generate measurable growth, and create meaningful customer experiences.',
      image1: '',
      image2: ''
    },
    expertise: {
      text: 'Our team combines strategic thinking, creative excellence, and technical expertise to deliver end-to-end solutions—from branding and graphic design to website development, mobile applications, digital advertising, content marketing, and business automation. Every project is tailored to align with our clients\' goals, ensuring impactful results and long-term success.',
      image1: '',
      image2: ''
    },
    partners: {
      text: 'We work with startups, SMEs, and established enterprises across diverse industries, providing innovative solutions that enhance visibility, strengthen brand identity, and drive business performance. By combining data-driven insights with creative storytelling, we create campaigns and digital experiences that engage audiences and deliver measurable ROI.',
      image1: '',
      image2: ''
    },
    approach: {
      text: 'At BigGroww, we don\'t just provide services—we build lasting partnerships. Our commitment to quality, transparency, innovation, and client success has made us a trusted growth partner for businesses looking to scale in today\'s competitive digital landscape.',
      image1: '',
      image2: ''
    },
    mission: {
      text: 'To empower businesses with innovative digital solutions, creative excellence, and technology-driven strategies that accelerate sustainable growth.',
      image1: '',
      image2: ''
    },
    vision: {
      text: 'To become a globally recognized digital transformation and creative agency, helping businesses of all sizes achieve their full potential through innovation, strategy, and exceptional execution.',
      image1: '',
      image2: ''
    },
    coreValues: [
      {
        title: 'Innovation with Purpose',
        description: 'We drive creativity combined with strategy to deliver meaningful digital experiences.',
        icon: 'Sparkles',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        title: 'Client-Centric Approach',
        description: 'Every strategy is tailored to align with our clients\' goals and their success.',
        icon: 'Heart',
        color: 'from-rose-500 to-pink-500'
      },
      {
        title: 'Transparency & Integrity',
        description: 'We build partnerships founded on complete trust, honesty, and shared ambition.',
        icon: 'Shield',
        color: 'from-emerald-500 to-teal-500'
      },
      {
        title: 'Excellence in Execution',
        description: 'We demand the highest quality standards, ensuring outstanding results in every project.',
        icon: 'Award',
        color: 'from-amber-500 to-orange-500'
      },
      {
        title: 'Continuous Growth & Learning',
        description: 'We embrace new digital technologies and techniques to stay ahead of the curve.',
        icon: 'TrendingUp',
        color: 'from-indigo-500 to-violet-500'
      },
      {
        title: 'Results That Matter',
        description: 'We focus on key outcomes, delivering measurable growth and a strong return on investment.',
        icon: 'CheckCircle',
        color: 'from-purple-500 to-fuchsia-500'
      }
    ],
    promise: {
      tagline: 'Our Promise',
      text: 'We don\'t simply deliver services—we build partnerships founded on trust, excellence, and shared ambition. Every strategy we develop, every design we create, and every solution we deliver is driven by one purpose: helping your business grow with confidence.',
      quote: 'BigGroww — Building Brands. Creating Impact. Inspiring Growth.'
    }
  },
  services: defaultServices,
  additionalServices: [
    {
      icon: 'Video',
      title: 'Video Marketing & Animation',
      description: 'We craft content that speaks directly to your audience. From blogs and copywriting to storytelling and campaigns, our content enhances your brand voice, boosts engagement, and strengthens your online presence while delivering real value to your customers.'
    },
    {
      icon: 'Users',
      title: 'Influencer & Affiliate Marketing.',
      description: 'We create high-quality videos and photography that showcase your brand\'s best side. With compelling visuals, storytelling, and professional production, we help you capture attention, build trust, and communicate your brand\'s essence across digital platforms'
    }
  ],
  portfolio: defaultPortfolio,
  testimonials: defaultTestimonials,
  customPages: [
    {
      id: 'faq',
      slug: 'faq',
      title: 'FAQ',
      content: `<h3>1. What does BigGroww actually do?</h3><p>BigGroww helps businesses grow with branding, websites, mobile apps, digital marketing, SEO, creative design, and AI automation—all under one roof.</p><h3>2. I'm starting a new business. Can you help from scratch?</h3><p>Yes! We can build everything from your logo and brand identity to your website, marketing strategy, and online presence.</p><h3>3. Can you build a website that brings in customers?</h3><p>Absolutely. We create fast, mobile-friendly, SEO-optimized websites designed to generate leads, sales, and business growth.</p><h3>4. Do you develop Android and iPhone apps?</h3><p>Yes. We design and develop custom Android and iOS apps with modern features, user-friendly interfaces, and secure backend systems.</p><h3>5. Will my website rank on Google?</h3><p>We build websites following SEO best practices and also offer ongoing SEO services to help improve your Google rankings over time.</p><h3>6. Can you run Google and Facebook ads for my business?</h3><p>Yes. We manage Google Ads, Facebook, Instagram, LinkedIn, and YouTube campaigns to help you reach the right audience and increase conversions.</p><h3>7. My business needs a new logo. Can you design one?</h3><p>Of course! We create unique logos and complete brand identities that make your business look professional and memorable.</p><h3>8. Do you only work with big companies?</h3><p>Not at all. We work with startups, small businesses, local brands, and established companies of all sizes.</p><h3>9. Can you redesign my existing website?</h3><p>Yes. If your current website looks outdated or performs poorly, we can redesign it with a fresh, modern, and responsive design.</p><h3>10. Do you provide website maintenance after launch?</h3><p>Yes. We handle updates, backups, security, bug fixes, and performance improvements so your website stays secure and up to date.</p><h3>11. What is AI automation, and how can it help my business?</h3><p>AI automation saves time by handling repetitive tasks like customer support, lead collection, appointment booking, and WhatsApp responses automatically.</p><h3>12. Can you build a chatbot for my website or WhatsApp?</h3><p>Yes. We create AI-powered chatbots that answer customer questions, collect leads, and provide 24/7 support.</p><h3>13. Do you help businesses get more leads online?</h3><p>Yes. We combine SEO, paid advertising, content marketing, and conversion optimization to generate quality leads for your business.</p><h3>14. Can you help with social media marketing?</h3><p>Yes. We create engaging content, manage social media pages, run ad campaigns, and help increase your brand's online visibility.</p><h3>15. Do I need my own domain and hosting?</h3><p>Yes, and we make it easy. We can register your domain, set up hosting, install SSL, and configure professional business email accounts.</p><h3>16. Can you improve my website's speed?</h3><p>Definitely. We optimize website speed, security, and performance to improve user experience and search engine rankings.</p><h3>17. Do you create business presentations and pitch decks?</h3><p>Yes. We design professional company profiles, investor pitch decks, sales presentations, and corporate presentations that leave a strong impression.</p><h3>18. How much does a website or app cost?</h3><p>The cost depends on your requirements. We provide customized quotes based on your business goals, features, and project scope.</p><h3>19. How long will my project take?</h3><p>Most websites are completed within 2–6 weeks, while mobile apps and larger custom projects may take longer depending on the complexity.</p><h3>20. Why should I choose BigGroww over other agencies?</h3><p>Because we don't just deliver designs or marketing—we build complete digital growth solutions. Our team focuses on creativity, technology, and measurable business results to help your brand grow with confidence.</p>`,
      isPublished: true
    }
  ]

};

export const magicalFonts = [
  'Outfit', 'Syne', 'Space Grotesk', 'Cinzel', 'Playfair Display', 
  'Montserrat', 'Bungee', 'Righteous', 'Uncial Antiqua', 'Fredericka the Great', 
  'Inter', 'Cabin Sketch', 'Syncopate', 'Quicksand', 'Permanent Marker', 
  'Cormorant Garamond', 'Josefin Sans', 'Comfortaa', 'DM Serif Display', 'Space Mono'
];

interface SiteConfigContextType {
  config: SiteConfig;
  updateConfig: (newConfig: SiteConfig) => void;
  isGetStartedOpen: boolean;
  openGetStarted: () => void;
  closeGetStarted: () => void;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);


/** Deep merge: stored values override defaults, but new default fields are always present */
function deepMerge<T>(base: T, override: Partial<T>): T {
  if (Array.isArray(base) && Array.isArray(override)) {
    // Arrays: if override is non-empty use it, otherwise use base
    return (override.length > 0 ? override : base) as unknown as T;
  }
  if (typeof base === 'object' && base !== null && typeof override === 'object' && override !== null) {
    const result: any = { ...base };
    for (const key of Object.keys(override) as (keyof T)[]) {
      if (key in base) {
        result[key] = deepMerge(base[key], (override as any)[key]);
      } else {
        result[key] = (override as any)[key];
      }
    }
    return result;
  }
  return override !== undefined ? (override as T) : base;
}

export function SiteConfigProvider({ children }: { children: React.ReactNode }) {
  const [isGetStartedOpen, setIsGetStartedOpen] = useState(false);
  const openGetStarted = () => setIsGetStartedOpen(true);
  const closeGetStarted = () => setIsGetStartedOpen(false);

  // ── Helper: run all migrations on a parsed config ─────────────────────────
  function migrateConfig(parsed: any): SiteConfig {
    if (!parsed) return defaultSiteConfig;

    // SMTP secrets must never persist in browser storage. Older browser caches
    // are scrubbed as they are read; the server retains the encrypted secret.
    if (parsed.smtp && typeof parsed.smtp === 'object') parsed.smtp.password = '';

    // Migrate old socials if needed
    if (parsed.contact?.socials) {
      const hasOldSocials = parsed.contact.socials.some((s: any) => s.platform === 'Google' || s.platform === 'YouTube');
      if (hasOldSocials) {
        parsed.contact.socials = [
          { platform: 'LinkedIn', url: 'https://linkedin.com' },
          { platform: 'Instagram', url: 'https://instagram.com' },
          { platform: 'Facebook', url: 'https://facebook.com' },
          { platform: 'GitHub', url: 'https://github.com' }
        ];
      }
    }
    // Migrate services
    if (parsed.services) {
      const firstServiceHasImage = parsed.services[0]?.image;
      if (!firstServiceHasImage) parsed.services = defaultServices;
    }
    // Migrate portfolio
    if (parsed.portfolio) {
      const hasAura = parsed.portfolio.some((p: any) => p.title === 'Aura Cosmetics E-Commerce');
      if (!hasAura) {
        parsed.portfolio = [
          { title: 'Lovixo Dating App', category: 'MOBILE APPLICATION / DATING', description: 'A modern dating platform with glassmorphism UI, floating animated elements, and interactive matchmaking cards.', image: '/biggrow/uploads/lovixo_dating_app.png', tags: 'Flutter, Dart, PHP' },
          { title: 'Krida Esports - Online Tournament Platform', category: 'GAMING / ESPORTS', description: 'An esports tournament hub featuring real-time leaderboards, tournament scheduling, and player profiles.', image: '/biggrow/uploads/krida_esports_app.png', tags: 'Java/Kotlin, Android SDK, PHP' },
          { title: 'Aura Cosmetics E-Commerce', category: 'RETAIL / E-COMMERCE', description: 'A premium minimalist online beauty store featuring immersive product displays and tailored virtual experience elements.', image: '/biggrow/uploads/aura_cosmetics_shop.png', tags: 'Next.js, Tailwind CSS, Shopify API' },
          { title: 'Apex Analytics SaaS Dashboard', category: 'TECHNOLOGY / FINTECH', description: 'A real-time financial tracking SaaS offering advanced AI-driven predictive insights, interactive chart sets, and custom reports.', image: '/biggrow/uploads/apex_analytics_dashboard.png', tags: 'React, TypeScript, Go, Tailwind CSS' }
        ];
      }
    }
    // Migrate FAQ content
    if (parsed.customPages) {
      const faqPage = parsed.customPages.find((p: any) => p.id === 'faq');
      if (faqPage && !faqPage.content.includes('What does BigGroww actually do?')) {
        faqPage.content = `<h3>1. What does BigGroww actually do?</h3><p>BigGroww helps businesses grow with branding, websites, mobile apps, digital marketing, SEO, creative design, and AI automation—all under one roof.</p><h3>2. I'm starting a new business. Can you help from scratch?</h3><p>Yes! We can build everything from your logo and brand identity to your website, marketing strategy, and online presence.</p><h3>3. Can you build a website that brings in customers?</h3><p>Absolutely. We create fast, mobile-friendly, SEO-optimized websites designed to generate leads, sales, and business growth.</p><h3>4. Do you develop Android and iPhone apps?</h3><p>Yes. We design and develop custom Android and iOS apps with modern features, user-friendly interfaces, and secure backend systems.</p><h3>5. Will my website rank on Google?</h3><p>We build websites following SEO best practices and also offer ongoing SEO services to help improve your Google rankings over time.</p><h3>6. Can you run Google and Facebook ads for my business?</h3><p>Yes. We manage Google Ads, Facebook, Instagram, LinkedIn, and YouTube campaigns to help you reach the right audience and increase conversions.</p><h3>7. My business needs a new logo. Can you design one?</h3><p>Of course! We create unique logos and complete brand identities that make your business look professional and memorable.</p><h3>8. Do you only work with big companies?</h3><p>Not at all. We work with startups, small businesses, local brands, and established companies of all sizes.</p><h3>9. Can you redesign my existing website?</h3><p>Yes. If your current website looks outdated or performs poorly, we can redesign it with a fresh, modern, and responsive design.</p><h3>10. Do you provide website maintenance after launch?</h3><p>Yes. We handle updates, backups, security, bug fixes, and performance improvements so your website stays secure and up to date.</p><h3>11. What is AI automation, and how can it help my business?</h3><p>AI automation saves time by handling repetitive tasks like customer support, lead collection, appointment booking, and WhatsApp responses automatically.</p><h3>12. Can you build a chatbot for my website or WhatsApp?</h3><p>Yes. We create AI-powered chatbots that answer customer questions, collect leads, and provide 24/7 support.</p><h3>13. Do you help businesses get more leads online?</h3><p>Yes. We combine SEO, paid advertising, content marketing, and conversion optimization to generate quality leads for your business.</p><h3>14. Can you help with social media marketing?</h3><p>Yes. We create engaging content, manage social media pages, run ad campaigns, and help increase your brand's online visibility.</p><h3>15. Do I need my own domain and hosting?</h3><p>Yes, and we make it easy. We can register your domain, set up hosting, install SSL, and configure professional business email accounts.</p><h3>16. Can you improve my website's speed?</h3><p>Definitely. We optimize website speed, security, and performance to improve user experience and search engine rankings.</p><h3>17. Do you create business presentations and pitch decks?</h3><p>Yes. We design professional company profiles, investor pitch decks, sales presentations, and corporate presentations that leave a strong impression.</p><h3>18. How much does a website or app cost?</h3><p>The cost depends on your requirements. We provide customized quotes based on your business goals, features, and project scope.</p><h3>19. How long will my project take?</h3><p>Most websites are completed within 2–6 weeks, while mobile apps and larger custom projects may take longer depending on the complexity.</p><h3>20. Why should I choose BigGroww over other agencies?</h3><p>Because we don't just deliver designs or marketing—we build complete digital growth solutions. Our team focuses on creativity, technology, and measurable business results to help your brand grow with confidence.</p>`;
      }
    }
    // Migrate about text fields to objects
    if (parsed.about) {
      const fields = ['whoWeAre', 'expertise', 'partners', 'approach', 'mission', 'vision'];
      for (const f of fields) {
        const val = parsed.about[f];
        if (typeof val === 'string') {
          parsed.about[f] = { text: val, image1: '', image2: '' };
        } else if (typeof val === 'object' && val !== null && typeof val.text === 'undefined') {
          const chars: string[] = [];
          let i = 0;
          while (typeof val[i] === 'string') { chars.push(val[i]); i++; }
          parsed.about[f] = { text: chars.join(''), image1: val.image1 || '', image2: val.image2 || '' };
        }
      }
    }
    return deepMerge(defaultSiteConfig, parsed);
  }

  // ── Init state from localStorage (fast, synchronous) ─────────────────────
  const [config, setConfig] = useState<SiteConfig>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('biggrow_site_config_v4');
      if (stored) {
        try {
          return migrateConfig(JSON.parse(stored));
        } catch { /* fall through */ }
      }
    }
    return defaultSiteConfig;
  });

  // ── On mount: fetch from API and sync if newer ────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/config.php`, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.config) {
          const migrated = migrateConfig(data.config);
          setConfig(migrated);
          // Also update localStorage cache
          localStorage.setItem('biggrow_site_config_v4', JSON.stringify(migrated));
        }
      })
      .catch(() => {
        // API unreachable (dev mode without XAMPP) — localStorage fallback already loaded
        console.info('[BigGroww] Config API unavailable, using localStorage cache.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Save to both API and localStorage on every change ────────────────────
  const updateConfig = (newConfig: SiteConfig) => {
    setConfig(newConfig);
    // 1. Always update localStorage immediately (instant, works offline)
    const browserCache = { ...newConfig, smtp: { ...newConfig.smtp, password: '' } };
    localStorage.setItem('biggrow_site_config_v4', JSON.stringify(browserCache));
    // 2. Persist to database via API (async, non-blocking)
    fetch(`${API_BASE}/config.php`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify(newConfig),
    }).catch(err => {
      console.warn('[BigGroww] Failed to save config to DB:', err);
    });
  };

  useEffect(() => {
    // 1. Dynamic Font Injection

    const font = config.theme.fontFamily;
    const fontUrlName = font.replace(/\s+/g, '+');
    const linkId = 'dynamic-google-font-link';
    let link = document.getElementById(linkId) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?family=${fontUrlName}:wght@300;400;500;700;900&display=swap`;

    // Inject global CSS rule to override hardcoded Tailwind .font-sans classes
    const styleId = 'dynamic-font-family-style';
    let styleTag = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      body, html, .font-sans, .font-serif, [class*="font-"] {
        font-family: '${font}', sans-serif !important;
      }
    `;

    // 2. Dynamic Colors Injection
    document.documentElement.style.setProperty('--primary', config.theme.primaryColor);
    document.documentElement.style.setProperty('--accent', config.theme.accentColor);
    document.documentElement.style.setProperty('--highlight', config.theme.highlightColor);
  }, [config.theme]);

  return (
    <SiteConfigContext.Provider value={{ config, updateConfig, isGetStartedOpen, openGetStarted, closeGetStarted }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

export const PROJECT_ROOT = (import.meta.env.BASE_URL || '/').replace(/dist\/$/, '');
export const API_BASE = PROJECT_ROOT + 'api';
export const UPLOADS_BASE = PROJECT_ROOT + 'uploads';

export function resolveUploadUrl(url: string | undefined): string {
  if (!url) return '';
  if (url.startsWith('/biggrow/uploads/')) {
    return url.replace('/biggrow/uploads', UPLOADS_BASE);
  }
  if (url.startsWith('/uploads/') && PROJECT_ROOT !== '/') {
    return PROJECT_ROOT + url.slice(1);
  }
  return url;
}
